import * as React from "react"
import styled from 'styled-components'

const Sheet = styled.div``

const SheetTrigger = styled.div``

const SheetClose = styled.div``

const SheetPortal = styled.div<{ $isOpen: boolean }>`
  position: ${props => props.$isOpen ? 'fixed' : 'static'};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${props => props.$isOpen ? 1001 : 'auto'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`

const SheetOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1001;
  background: rgba(0, 0, 0, 0.8);
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`

const SheetContent = styled.div<{ side?: 'top' | 'right' | 'bottom' | 'left'; $isOpen: boolean }>`
  position: fixed;
  z-index: 1002;
  gap: 1rem;
  background: var(--color-white);
  padding: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  
  ${props => {
    const isOpen = props.$isOpen;
    switch (props.side) {
      case 'top':
        return `
          inset: 0 0 auto 0;
          border-bottom: 2px solid var(--color-light-gray);
          transform: translateY(${isOpen ? '0' : '-100%'});
        `;
      case 'bottom':
        return `
          inset: auto 0 0 0;
          border-top: 2px solid var(--color-light-gray);
          transform: translateY(${isOpen ? '0' : '100%'});
        `;
      case 'left':
        return `
          inset: 0 auto 0 0;
          height: 100%;
          width: 75%;
          max-width: 384px;
          border-right: 2px solid var(--color-light-gray);
          transform: translateX(${isOpen ? '0' : '-100%'});
        `;
      case 'right':
      default:
        return `
          inset: 0 0 0 auto;
          height: 100%;
          width: 75%;
          max-width: 384px;
          border-left: 2px solid var(--color-light-gray);
          transform: translateX(${isOpen ? '0' : '100%'});
        `;
    }
  }}
`

const SheetHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  
  @media (min-width: 640px) {
    text-align: left;
  }
`

const SheetFooter = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 0.5rem;
  margin-top: 1.5rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`

const SheetTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-black);
  line-height: 1;
  letter-spacing: -0.025em;
  margin: 0;
`

const SheetDescription = styled.p`
  font-size: 0.875rem;
  color: var(--color-gray);
  line-height: 1.4;
  margin: 0;
`

interface SheetProps extends React.HTMLAttributes<HTMLDivElement> {
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

interface SheetTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
}

interface SheetCloseProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
}

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left'
}

interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

interface SheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

interface SheetTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

interface SheetDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const SheetContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
}>({
  open: false,
  setOpen: () => {}
})

const useSheet = () => {
  const context = React.useContext(SheetContext)
  if (!context) {
    throw new Error('useSheet must be used within a Sheet')
  }
  return context
}

const SheetProvider = ({ children, onOpenChange, open: controlledOpen, ...props }: SheetProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  
  const handleSetOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }
  
  return (
    <SheetContext.Provider value={{ open, setOpen: handleSetOpen }}>
      <Sheet {...props}>{children}</Sheet>
    </SheetContext.Provider>
  )
}

const SheetTriggerComponent = React.forwardRef<HTMLDivElement, SheetTriggerProps>(
  ({ asChild, children, onClick, ...props }, ref) => {
    const { setOpen } = useSheet()
    
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      setOpen(true)
      onClick?.(e)
    }
    
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        onClick: handleClick
      } as any)
    }
    
    return (
      <SheetTrigger ref={ref} onClick={handleClick} {...props}>
        {children}
      </SheetTrigger>
    )
  }
)
SheetTriggerComponent.displayName = 'SheetTrigger'

const SheetCloseComponent = React.forwardRef<HTMLDivElement, SheetCloseProps>(
  ({ asChild, children, onClick, ...props }, ref) => {
    const { setOpen } = useSheet()
    
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      setOpen(false)
      onClick?.(e)
    }
    
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        onClick: handleClick
      } as any)
    }
    
    return (
      <SheetClose ref={ref} onClick={handleClick} {...props}>
        {children}
      </SheetClose>
    )
  }
)
SheetCloseComponent.displayName = 'SheetClose'

const SheetContentComponent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ side = 'right', children, ...props }, ref) => {
    const { open, setOpen } = useSheet()
    
    React.useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setOpen(false)
        }
      }
      
      if (open) {
        document.addEventListener('keydown', handleEscape)
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'unset'
      }
      
      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = 'unset'
      }
    }, [open, setOpen])
    
    return (
      <SheetPortal $isOpen={open}>
        <SheetOverlay $isOpen={open} onClick={() => setOpen(false)} />
        <SheetContent ref={ref} side={side} $isOpen={open} {...props}>
          {children}
        </SheetContent>
      </SheetPortal>
    )
  }
)
SheetContentComponent.displayName = 'SheetContent'

const SheetHeaderComponent = React.forwardRef<HTMLDivElement, SheetHeaderProps>(
  ({ ...props }, ref) => {
    return <SheetHeader ref={ref} {...props} />
  }
)
SheetHeaderComponent.displayName = 'SheetHeader'

const SheetFooterComponent = React.forwardRef<HTMLDivElement, SheetFooterProps>(
  ({ ...props }, ref) => {
    return <SheetFooter ref={ref} {...props} />
  }
)
SheetFooterComponent.displayName = 'SheetFooter'

const SheetTitleComponent = React.forwardRef<HTMLHeadingElement, SheetTitleProps>(
  ({ ...props }, ref) => {
    return <SheetTitle ref={ref} {...props} />
  }
)
SheetTitleComponent.displayName = 'SheetTitle'

const SheetDescriptionComponent = React.forwardRef<HTMLParagraphElement, SheetDescriptionProps>(
  ({ ...props }, ref) => {
    return <SheetDescription ref={ref} {...props} />
  }
)
SheetDescriptionComponent.displayName = 'SheetDescription'

export {
  SheetProvider as Sheet,
  SheetTriggerComponent as SheetTrigger,
  SheetCloseComponent as SheetClose,
  SheetContentComponent as SheetContent,
  SheetHeaderComponent as SheetHeader,
  SheetFooterComponent as SheetFooter,
  SheetTitleComponent as SheetTitle,
  SheetDescriptionComponent as SheetDescription,
}
